import React, {useEffect, useState} from 'react';
import {DatePicker, Flex, Select} from "antd";
import {t} from "i18next";
import {debounce} from "lodash";
import Protected from "../../roles/Protected";
import {BriefFilter, UserValue} from "./index";
import ButtonBlack from "../../components/ui/buttons/ButtonBlack";
import type {DefaultOptionType} from "antd/es/select";
import useSWR from "swr";
import {api, HydraData} from "../../api/apiProvider";
import urls from "../../api/urls";

const {RangePicker} = DatePicker

interface Model {
  id: string,
  '@id'?: string,
  name: string,
}

interface FilterProps {
  briefFilter: BriefFilter,
  setBriefFilter: Function,
}

export default function Filter({briefFilter, setBriefFilter}: FilterProps) {
  const [filter, setFilter] = useState<BriefFilter>(briefFilter)
  const [poolSearch, setPoolSearch] = useState<string>('')
  const [companySearch, setCompanySearch] = useState<string>('')
  const [productSearch, setProductSearch] = useState<string>('')
  const [poolsOptions, setPoolsOptions] = useState<DefaultOptionType[]>([])
  const [companiesOptions, setCompaniesOptions] = useState<DefaultOptionType[]>([])
  const [productsOptions, setProductsOptions] = useState<DefaultOptionType[]>([])
  const [statusesOptions, setStatusesOptions] = useState<DefaultOptionType[]>([
    {
      label: t('gant.all'),
      value: 'all',
    },
    {
      label: t('gant.inProgress'),
      value: 'inWork',
    },
    {
      label: t('gant.completed'),
      value: 'completed',
    },
  ])

  const {
    data: pools,
  } = useSWR<HydraData>(poolSearch ? `${urls.pools}?name=${poolSearch}&pagination=false` : `${urls.pools}?pagination=false`, api.get, {
    revalidateOnFocus: false
  })

  const getCompaniesParamsString = () => {
    const params = new URLSearchParams()

    if (companySearch) {
      params.append('name', companySearch)
    }

    if (filter.pool) {
      params.append('pool.id', String(filter.pool.value))
    }

    params.append('pagination', String(false))

    return params.toString()
  }

  const {
    data: companies,
  } = useSWR<HydraData>(`${urls.companies}?${getCompaniesParamsString()}`, api.get, {
    revalidateOnFocus: false
  })

  const getProductsParamsString = () => {
    const params = new URLSearchParams()

    if (productSearch) {
      params.append('name', productSearch)
    }

    if (filter.company) {
      params.append('company.id', String(filter.company.value))
    }

    params.append('pagination', String(false))

    return params.toString()
  }

  const {
    data: products,
  } = useSWR<HydraData>(`${urls.products}?${getProductsParamsString()}`, api.get, {
    revalidateOnFocus: false
  })

  useEffect(() => {
    if (pools && pools.data) {
      setPoolsOptions(pools.data.map((pool: Model) => {
        return {
          value: pool.id,
          label: pool.name,
        }
      }))
    }
  }, [pools])

  useEffect(() => {
    if (companies && companies.data) {
      setCompaniesOptions(companies.data.map((company: Model) => {
        return {
          value: company.id,
          label: company.name,
        }
      }))
    }
  }, [companies])

  useEffect(() => {
    if (products && products.data) {
      setProductsOptions(products.data.map((product: Model) => {
        return {
          value: product.id,
          label: product.name,
        }
      }))
    }
  }, [products])

  const submit = () => {
    setBriefFilter(filter)
  }

  return (
    <Flex className={'gant-filter'}>
      <Flex style={{width: '100%'}} align={'center'}>
        <Flex gap={'small'} align={'flex-end'}>
          <Flex vertical gap={6}>
            <Flex className={'gant-filter__title'}>
              {t('gant.status')}
            </Flex>
            <Select
              style={{width: 200}}
              allowClear
              labelInValue
              placeholder={t('gant.status')}
              optionFilterProp="children"
              onChange={(data: UserValue) => {
                setFilter({
                  ...filter,
                  status: data,
                })
              }}
              filterOption={false}
              options={statusesOptions}
              value={filter.status}
              size={'large'}
            />
          </Flex>
          <Flex vertical gap={6}>
            <Flex className={'gant-filter__title'}>
              {t('brief.pool')}
            </Flex>
            <Select
              style={{width: 200}}
              showSearch
              allowClear
              labelInValue
              placeholder={t('brief.pool')}
              optionFilterProp="children"
              onChange={(data: UserValue) => {
                delete filter.company
                delete filter.products
                setFilter({
                  ...filter,
                  pool: data,
                })
              }}
              onSearch={debounce((value: string) => {
                setPoolSearch(value)
              }, 600)}
              filterOption={false}
              options={poolsOptions}
              value={filter.pool}
              size={'large'}
            />
          </Flex>
          <Flex vertical gap={6}>
            <Flex className={'gant-filter__title'}>
              {t('brief.company')}
            </Flex>
            <Select
              style={{width: 200}}
              showSearch
              allowClear
              labelInValue
              placeholder={t('brief.company')}
              optionFilterProp="children"
              onChange={(data: UserValue) => {
                delete filter.products
                setFilter({
                  ...filter,
                  company: data,
                })
              }}
              onSearch={debounce((value: string) => setCompanySearch(value), 600)}
              filterOption={false}
              options={companiesOptions}
              value={filter.company}
              size={'large'}
            />
          </Flex>
          <Flex vertical gap={6}>
            <Flex className={'gant-filter__title'}>
              {t('brief.product')}
            </Flex>
            <Select
              style={{width: 200}}
              showSearch
              allowClear
              labelInValue
              placeholder={t('brief.product')}
              optionFilterProp="children"
              mode="multiple"
              onChange={(data: UserValue[]) => {
                setFilter({
                  ...filter,
                  products: data,
                })
              }}
              onSearch={debounce((value: string) => setProductSearch(value), 600)}
              filterOption={false}
              options={productsOptions}
              value={filter.products}
              size={'large'}
            />
          </Flex>
          <Flex vertical gap={6}>
            <Flex className={'gant-filter__title'}>
              {t('brief.period')}
            </Flex>
            <RangePicker
              size={'large'}
              placeholder={[t('list.dateStart'), t('list.dateEnd')]}
              onChange={(date) => {
                if (date) {
                  setFilter({
                    ...filter,
                    period: date,
                  })
                } else {
                  setFilter({
                    ...filter,
                    period: null,
                  })
                }
              }}
              format={'DD.MM.YYYY'}
            />
          </Flex>
          <ButtonBlack
            className={'gant-filter__button'}
            onClick={submit}
            size={'large'}
          >{t('gant.apply')}</ButtonBlack>
        </Flex>
      </Flex>
    </Flex>
  );
}
